// The core shell (mockup 3): top bar with the project switcher and bell, a five-slot
// tab bar on the phone, a sidebar on the desktop. Nav comes from module manifests.
import { NavLink, useLocation } from 'react-router';
import type { ReactNode } from 'react';
import { navFor } from '@tradeview/core';
import { useAuth } from '../auth';
import { useUpdateAvailable, applyUpdate } from '../update';
import { Icon } from './Icon';
import { HelpButton } from './Help';
import { Tour } from './Tour';
import { useAlerts, unreadCount } from './alerts';

const CORE_NAV = [
  { id: 'home', label: 'Today', icon: 'home', route: '/', order: 0, where: ['phone', 'desktop'] as const },
];
const MORE = { id: 'more', label: 'More', icon: 'more', route: '/modules', order: 999 };
// The desktop sidebar is grouped the way the day runs, not the way the modules were built:
// Field in the order of a day on site, Office in the order of a morning at the desk, Company
// pinned at the bottom. A module missing from a list falls to the end of its group by its own
// order, so a new module never shuffles the ones people have learned.
export const FIELD_ORDER = ['home', 'clock', 'records', 'reports', 'channel', 'drawings', 'materials', 'punch', 'schedule'];
export const OFFICE_ORDER = ['plan', 'mail', 'controls', 'procurement', 'billing', 'costing', 'bids', 'service', 'warehouse', 'sops', 'analytics'];
type NavItem = { id: string; label: string; icon: string; route: string; order: number; bundle?: string };
function grouped(items: NavItem[]) {
  const rank = (list: string[], n: NavItem) => { const i = list.indexOf(n.id); return i === -1 ? 100 + n.order : i; };
  const field = items.filter(n => !n.bundle || n.bundle === 'core' || n.bundle === 'field').sort((a, b) => rank(FIELD_ORDER, a) - rank(FIELD_ORDER, b));
  const office = items.filter(n => n.bundle && n.bundle !== 'core' && n.bundle !== 'field').sort((a, b) => rank(OFFICE_ORDER, a) - rank(OFFICE_ORDER, b));
  return { field, office };
}

export function Shell({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const role = session?.role ?? null;
  const ent = session?.entitlements ?? null;
  const phone = [...CORE_NAV, ...navFor('phone', role, ent)].sort((a, b) => a.order - b.order);
  const desktop = [...CORE_NAV, ...navFor('desktop', role, ent)].sort((a, b) => a.order - b.order);
  const tabs = [...phone.slice(0, 4), MORE];
  const { pathname } = useLocation();
  const update = useUpdateAvailable();
  const unread = unreadCount(useAlerts());

  return (
    <div className="min-h-full md:grid md:grid-cols-[220px_1fr]">
      <aside className="hidden md:flex flex-col bg-surface border-r border-line sticky top-0 h-screen">
        <div className="flex items-center gap-2 px-4 pt-3 pb-2.5 shrink-0">
          <div className="w-[34px] h-[34px] rounded-[9px] bg-accent text-accent-ink grid place-items-center font-disp font-bold text-lg">T</div>
          <div className="min-w-0"><div className="lbl">TradeView</div><b className="font-head block truncate">{session?.displayName}</b></div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-3 flex flex-col gap-0.5" style={{ scrollbarWidth: 'thin' }}>
          {(() => { const g = grouped(desktop); return (
            <>
              <div className="lbl px-2.5 pt-1 pb-1">Field</div>
              {g.field.map(n => <SideLink key={n.id} to={n.route} icon={n.icon} label={n.label} />)}
              {g.office.length > 0 && <div className="lbl px-2.5 pt-3 pb-1">Office</div>}
              {g.office.map(n => <SideLink key={n.id} to={n.route} icon={n.icon} label={n.label} />)}
            </>
          ); })()}
        </div>
        <div className="shrink-0 px-3 pt-2 pb-3 border-t border-line flex flex-col gap-0.5">
          <div className="lbl px-2.5 pb-1">Company</div>
          <SideLink to="/modules" icon="grid" label="Modules" />
          <SideLink to="/settings" icon="settings" label="Settings" />
        </div>
      </aside>

      <div className="min-h-full flex flex-col">
        <header className="flex items-center justify-between gap-2.5 px-4 pt-3.5 pb-2 md:px-6">
          <div className="flex items-center gap-2 min-w-0 md:hidden">
            <div className="w-[34px] h-[34px] rounded-[9px] bg-accent text-accent-ink grid place-items-center font-disp font-bold text-lg shrink-0">T</div>
            <div className="min-w-0">
              <div className="lbl">TradeView</div>
              <b className="font-head block truncate text-[15px]">{session?.displayName}</b>
            </div>
          </div>
          <div className="hidden md:block lbl">{labelFor(pathname, [...desktop, MORE])}</div>
          <div className="flex items-center gap-1.5 shrink-0"><HelpButton /><NavLink to="/alerts" className="w-9 h-9 rounded-full bg-surface border border-line grid place-items-center relative shrink-0" aria-label={unread ? `${unread} unread alerts` : 'Alerts'}>
            <Icon name="bell" />
            {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-crit text-white text-[11px] font-head font-bold grid place-items-center leading-none">{unread > 99 ? '99+' : unread}</span>}
          </NavLink></div>
        </header>
        <Tour />

        {update && (
          <div className="mx-3.5 md:mx-6 mb-1 max-w-[1100px] w-auto lg:mx-auto flex items-center gap-2.5 bg-accent-soft text-accent rounded-[12px] px-3 py-2 text-[13.5px]" role="status">
            <Icon name="ai" size={18} className="shrink-0" /><span className="flex-1"><b className="font-head">A new version of TradeView is ready.</b></span>
            <button className="btn btn-sm btn-primary" onClick={applyUpdate}>Refresh</button>
          </div>
        )}
        <main className="flex-1 px-3.5 pb-[calc(9.5rem_+_env(safe-area-inset-bottom,0px))] md:px-6 md:pb-8 max-w-[1100px] w-full mx-auto">{children}</main>

        <nav className="md:hidden fixed bottom-0 inset-x-0 bg-surface border-t border-line grid grid-cols-5 px-1.5 pt-2" style={{ paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))' }} aria-label="Primary">
          {tabs.map(t => (
            <NavLink key={t.id} to={t.route} end={t.route === '/'} className={({ isActive }) => `flex flex-col items-center gap-0.5 font-head font-semibold text-[10.5px] tracking-wide ${isActive ? 'text-accent' : 'text-muted'}`}>
              <Icon name={t.icon} size={22} />{t.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}

function SideLink({ to, icon, label }: { to: string; icon: string; label: string }) {
  return (
    <NavLink to={to} end={to === '/'} className={({ isActive }) => `flex items-center gap-2.5 min-h-[34px] px-2.5 rounded-[9px] font-head font-semibold text-[13px] ${isActive ? 'bg-accent-soft text-accent' : 'text-muted hover:bg-bg'}`}>
      <Icon name={icon} size={18} />{label}
    </NavLink>
  );
}

function labelFor(path: string, items: { route: string; label: string }[]) {
  const hit = items.filter(i => i.route === '/' ? path === '/' : path.startsWith(i.route)).sort((a, b) => b.route.length - a.route.length)[0];
  return hit?.label ?? '';
}
